import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "rewards-content" }
const _hoisted_2 = { class: "text-h6 text-primary font-weight-medium" }
const _hoisted_3 = { class: "mr-1" }
const _hoisted_4 = {
  key: 0,
  class: "d-flex justify-center align-center py-8"
}
const _hoisted_5 = {
  key: 0,
  class: "d-flex flex-column align-center justify-center py-12"
}
const _hoisted_6 = { class: "d-flex align-center justify-center fill-height" }
const _hoisted_7 = { class: "text-truncate" }
const _hoisted_8 = { class: "text-body-2 text-medium-emphasis mb-3 text-truncate-2-lines" }
const _hoisted_9 = { class: "d-flex justify-space-between align-center" }
const _hoisted_10 = { class: "mr-1" }
const _hoisted_11 = { class: "text-h5" }
const _hoisted_12 = {
  key: 0,
  class: "mt-2"
}
const _hoisted_13 = ["src"]

import { ref, onMounted } from 'vue'
import MainLayout from './components/MainLayout.vue'
import { useStatsStore } from './stores/statsStore'
import { useWorkspaceStore } from './stores/workspaceStore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage } from './firebase'
import { useRouter } from 'vue-router'

interface StoreItem {
  id: string
  name: string
  description: string
  points: number
  quantity: number
  imageUrl: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'RewardsStore',
  setup(__props) {

const storeItems = ref<StoreItem[]>([])
const loading = ref(true)
const error = ref('')
const showAddItemModal = ref(false)
const editingItem = ref<StoreItem | null>(null)
const itemForm = ref({
  name: '',
  description: '',
  points: 0,
  quantity: 0,
  imageUrl: ''
})
const imageFile = ref<File | null>(null)
const imagePreview = ref<string | null>(null)
const saving = ref(false)
const showConfirmModal = ref(false)
const selectedItem = ref<StoreItem | null>(null)
const router = useRouter()

const { stats, refreshStats } = useStatsStore()
const { fetchWorkspaceEmoji, getWorkspaceEmoji } = useWorkspaceStore()
const workspaceEmoji = getWorkspaceEmoji()

// Image handling functions
const handleImageChange = (file: File | null) => {
  if (!file) {
    imagePreview.value = null
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    imagePreview.value = e.target?.result as string
  }
  reader.readAsDataURL(file)
}

const uploadImage = async (file: File): Promise<string | null> => {
  try {
    const timestamp = Date.now()
    const fileName = `store-items/${timestamp}-${file.name}`
    const fileRef = storageRef(storage, fileName)
    
    await uploadBytes(fileRef, file)
    const downloadUrl = await getDownloadURL(fileRef)
    return downloadUrl
  } catch (error) {
    console.error('Error uploading image:', error)
    return null
  }
}

const clearImage = () => {
  imageFile.value = null
  imagePreview.value = null
  itemForm.value.imageUrl = ''
}

onMounted(async () => {
  console.log('RewardsStore component mounted')
  try {
    // Workspace emoji is already loaded at app level for authenticated users
    console.log('Loading store items...')
    await fetchStoreItems()
    console.log('Store items loaded successfully')
  } catch (error) {
    console.error('Error loading RewardsStore data:', error)
    // Continue anyway, will show error states
  }
})

async function fetchStoreItems() {
  loading.value = true
  error.value = ''
  
  try {
    console.log('Fetching store items...')
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/store/items`,
      { credentials: 'include' }
    )
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('Error response:', response.status, response.statusText, errorData)
      throw new Error(`Failed to fetch store items: ${response.status} ${response.statusText}`)
    }
    
    const items = await response.json()
    console.log('Fetched store items:', items)
    
    // Validate items structure
    if (!Array.isArray(items)) {
      console.error('Invalid items response:', items)
      throw new Error('Invalid response format')
    }
    
    storeItems.value = items.map(item => ({
      id: item.id || '',
      name: item.name || '',
      description: item.description || '',
      points: typeof item.points === 'number' ? item.points : 0,
      quantity: typeof item.quantity === 'number' ? item.quantity : 0,
      imageUrl: item.imageUrl || null
    }))
    console.log('Processed store items:', storeItems.value)
  } catch (e) {
    error.value = 'Failed to load store items. Please try again later.'
    console.error('Error fetching store items:', e)
  } finally {
    loading.value = false
  }
}

function editItem(item: StoreItem) {
  editingItem.value = item
  itemForm.value = { ...item }
  showAddItemModal.value = true
}

async function saveItem() {
  try {
    saving.value = true
    
    // Upload image if selected
    let imageUrl = itemForm.value.imageUrl
    if (imageFile.value) {
      const uploadedUrl = await uploadImage(imageFile.value)
      if (uploadedUrl) {
        imageUrl = uploadedUrl
      }
    }

    const itemData = {
      name: itemForm.value.name,
      description: itemForm.value.description,
      points: itemForm.value.points,
      quantity: itemForm.value.quantity,
      imageUrl: imageUrl
    }

    const endpoint = editingItem.value
      ? `${process.env.VUE_APP_API_BASE_URL}/store/${editingItem.value.id}`
      : `${process.env.VUE_APP_API_BASE_URL}/store`

    const method = editingItem.value ? 'PUT' : 'POST'

    const response = await fetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(itemData)
    })

    if (!response.ok) {
      throw new Error('Failed to save item')
    }

    // Refresh store items
    await fetchStoreItems()
    showAddItemModal.value = false
    clearImage()
    itemForm.value = {
      name: '',
      description: '',
      points: 0,
      quantity: 0,
      imageUrl: ''
    }
  } catch (err) {
    console.error('Error saving item:', err)
    error.value = 'Failed to save item'
  } finally {
    saving.value = false
  }
}

function redeemItem(item: StoreItem) {
  console.log('Attempting to redeem item:', item)
  if (!item.id) {
    console.error('Item has no ID:', item)
    alert('Invalid item')
    return
  }
  selectedItem.value = item
  showConfirmModal.value = true
}

async function confirmRedeem() {
  if (!selectedItem.value?.id) {
    console.error('No valid item selected for redemption')
    return
  }
  
  try {
    console.log('Confirming redemption for item:', selectedItem.value)
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/store/items/${selectedItem.value.id}/redeem`,
      {
        method: 'POST',
        credentials: 'include'
      }
    )
    
    console.log('Redemption response:', response.status, response.statusText)
    
    const errorData = await response.json().catch(() => ({}))
    console.log('Response data:', errorData)
    
    if (!response.ok) {
      console.error('Redemption error data:', errorData)
      let errorMessage = 'Failed to redeem item'
      
      if (response.status === 401) {
        errorMessage = 'Please sign in to redeem items'
      } else if (response.status === 400) {
        errorMessage = errorData.error || 'Not enough points to redeem this item'
      } else if (response.status === 404) {
        errorMessage = 'Item not found or no longer available'
      }
      
      throw new Error(errorMessage)
    }
    
    await Promise.all([
      fetchStoreItems(),
      refreshStats() // Refresh global stats
    ])
    showConfirmModal.value = false
    selectedItem.value = null
    
    // Show success message
    alert('Item redeemed successfully!')
  } catch (e) {
    console.error('Error redeeming item:', e)
    alert(e.message || 'Failed to redeem item. Please try again.')
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_img = _resolveComponent("v-img")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(MainLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_v_card, {
          elevation: "2",
          class: "mb-6"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_text, { class: "d-flex justify-space-between align-center" }, {
              default: _withCtx(() => [
                _cache[9] || (_cache[9] = _createElementVNode("h2", { class: "text-h5 font-weight-bold" }, "Your Tokens", -1)),
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("span", _hoisted_3, _toDisplayString(_unref(workspaceEmoji).emojiSymbol || '🙏'), 1),
                  _createTextVNode(" " + _toDisplayString(_unref(stats).redeemablePoints) + " tokens available ", 1)
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_v_card, { elevation: "2" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center pa-4" }, {
              default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                _createElementVNode("div", null, [
                  _createElementVNode("span", { class: "text-h5 font-weight-bold" }, "Rewards Store")
                ], -1)
              ]))]),
              _: 1
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => [
                (loading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createVNode(_component_v_progress_circular, {
                        indeterminate: "",
                        color: "primary"
                      })
                    ]))
                  : (error.value)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 1,
                        type: "error",
                        class: "my-4"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(error.value), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                (!loading.value && !error.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                      (storeItems.value.length === 0)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                            _createVNode(_component_v_icon, {
                              size: "48",
                              color: "grey-lighten-1",
                              class: "mb-4"
                            }, {
                              default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                _createTextVNode("mdi-gift-off-outline", -1)
                              ]))]),
                              _: 1
                            }),
                            _cache[12] || (_cache[12] = _createElementVNode("div", { class: "text-h6 text-medium-emphasis mb-2" }, "No rewards available", -1)),
                            _cache[13] || (_cache[13] = _createElementVNode("div", { class: "text-body-2 text-grey" }, "Check back later for new rewards to claim!", -1))
                          ]))
                        : (_openBlock(), _createBlock(_component_v_row, { key: 1 }, {
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(storeItems.value, (item) => {
                                return (_openBlock(), _createBlock(_component_v_col, {
                                  key: item.id,
                                  cols: "12",
                                  sm: "12",
                                  md: "6"
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_card, {
                                      elevation: "1",
                                      class: "h-100",
                                      rounded: "lg",
                                      hover: true
                                    }, {
                                      default: _withCtx(() => [
                                        (item.imageUrl)
                                          ? (_openBlock(), _createBlock(_component_v_img, {
                                              key: 0,
                                              src: item.imageUrl,
                                              alt: item.name,
                                              height: "200",
                                              cover: "",
                                              class: "bg-grey-lighten-2"
                                            }, null, 8, ["src", "alt"]))
                                          : (_openBlock(), _createBlock(_component_v_img, {
                                              key: 1,
                                              height: "200",
                                              cover: "",
                                              class: "bg-grey-lighten-2"
                                            }, {
                                              default: _withCtx(() => [
                                                _createElementVNode("div", _hoisted_6, [
                                                  _createVNode(_component_v_icon, {
                                                    size: "48",
                                                    color: "grey-lighten-1",
                                                    icon: "mdi-image-outline"
                                                  })
                                                ])
                                              ]),
                                              _: 1
                                            })),
                                        _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center pt-4 px-4" }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("span", _hoisted_7, _toDisplayString(item.name), 1)
                                          ]),
                                          _: 2
                                        }, 1024),
                                        _createVNode(_component_v_card_text, { class: "pt-2" }, {
                                          default: _withCtx(() => [
                                            _createElementVNode("p", _hoisted_8, _toDisplayString(item.description), 1),
                                            _createElementVNode("div", _hoisted_9, [
                                              _createVNode(_component_v_chip, {
                                                color: "primary",
                                                variant: "flat",
                                                size: "small",
                                                class: "font-weight-medium"
                                              }, {
                                                prepend: _withCtx(() => [
                                                  _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(workspaceEmoji).emojiSymbol || '🙏'), 1)
                                                ]),
                                                default: _withCtx(() => [
                                                  _createTextVNode(" " + _toDisplayString(item.points) + " tokens ", 1)
                                                ]),
                                                _: 2
                                              }, 1024),
                                              _createVNode(_component_v_chip, {
                                                color: "grey",
                                                variant: "flat",
                                                size: "small",
                                                class: "font-weight-medium"
                                              }, {
                                                default: _withCtx(() => [
                                                  _createTextVNode(_toDisplayString(item.quantity) + " available ", 1)
                                                ]),
                                                _: 2
                                              }, 1024)
                                            ]),
                                            _createVNode(_component_v_btn, {
                                              block: "",
                                              color: "primary",
                                              class: "mt-4",
                                              disabled: _unref(stats).redeemablePoints < item.points || item.quantity === 0,
                                              onClick: ($event: any) => (redeemItem(item))
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(" Redeem for " + _toDisplayString(item.points) + " tokens ", 1)
                                              ]),
                                              _: 2
                                            }, 1032, ["disabled", "onClick"])
                                          ]),
                                          _: 2
                                        }, 1024)
                                      ]),
                                      _: 2
                                    }, 1024)
                                  ]),
                                  _: 2
                                }, 1024))
                              }), 128))
                            ]),
                            _: 1
                          }))
                    ], 64))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_v_dialog, {
          modelValue: showAddItemModal.value,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showAddItemModal).value = $event)),
          "max-width": "500px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_title, null, {
                  default: _withCtx(() => [
                    _createElementVNode("span", _hoisted_11, _toDisplayString(editingItem.value ? 'Edit' : 'Add') + " Item", 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_form, {
                      onSubmit: _withModifiers(saveItem, ["prevent"])
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: itemForm.value.name,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((itemForm.value.name) = $event)),
                          label: "Name",
                          required: "",
                          variant: "outlined",
                          density: "comfortable"
                        }, null, 8, ["modelValue"]),
                        _createVNode(_component_v_textarea, {
                          modelValue: itemForm.value.description,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((itemForm.value.description) = $event)),
                          label: "Description",
                          variant: "outlined",
                          density: "comfortable",
                          rows: "3"
                        }, null, 8, ["modelValue"]),
                        _createVNode(_component_v_text_field, {
                          modelValue: itemForm.value.points,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((itemForm.value.points) = $event)),
                          modelModifiers: { number: true },
                          label: "Tokens",
                          type: "number",
                          required: "",
                          min: "1",
                          variant: "outlined",
                          density: "comfortable"
                        }, null, 8, ["modelValue"]),
                        _createVNode(_component_v_text_field, {
                          modelValue: itemForm.value.quantity,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((itemForm.value.quantity) = $event)),
                          modelModifiers: { number: true },
                          label: "Quantity",
                          type: "number",
                          required: "",
                          min: "0",
                          variant: "outlined",
                          density: "comfortable"
                        }, null, 8, ["modelValue"]),
                        _createVNode(_component_v_file_input, {
                          modelValue: imageFile.value,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((imageFile).value = $event)),
                          label: "Image",
                          accept: "image/*",
                          variant: "outlined",
                          density: "comfortable",
                          "prepend-icon": "mdi-camera",
                          "show-size": true,
                          rules: [v => !editingItem.value || v || imagePreview.value ? true : 'Image is required'],
                          onChange: handleImageChange
                        }, {
                          selection: _withCtx(({ fileNames }) => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fileNames, (fileName) => {
                              return (_openBlock(), _createBlock(_component_v_chip, {
                                key: fileName,
                                size: "small",
                                label: "",
                                color: "primary",
                                class: "me-2"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(fileName), 1)
                                ]),
                                _: 2
                              }, 1024))
                            }), 128))
                          ]),
                          _: 1
                        }, 8, ["modelValue", "rules"]),
                        (imagePreview.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                              _createElementVNode("img", {
                                src: imagePreview.value,
                                alt: "Preview",
                                class: "preview-image"
                              }, null, 8, _hoisted_13),
                              (imagePreview.value)
                                ? (_openBlock(), _createBlock(_component_v_btn, {
                                    key: 0,
                                    icon: "mdi-close",
                                    size: "small",
                                    color: "error",
                                    class: "mt-2",
                                    onClick: clearImage
                                  }))
                                : _createCommentVNode("", true)
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card_actions, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_spacer),
                    _createVNode(_component_v_btn, {
                      color: "grey-darken-1",
                      variant: "text",
                      onClick: _cache[5] || (_cache[5] = ($event: any) => (showAddItemModal.value = false))
                    }, {
                      default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                        _createTextVNode(" Cancel ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_component_v_btn, {
                      color: "primary",
                      variant: "flat",
                      onClick: saveItem
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(editingItem.value ? 'Save' : 'Add'), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_dialog, {
          modelValue: showConfirmModal.value,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((showConfirmModal).value = $event)),
          "max-width": "500px"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_title, null, {
                  default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                    _createElementVNode("span", { class: "text-h5" }, "Confirm Redemption", -1)
                  ]))]),
                  _: 1
                }),
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => [
                    _createTextVNode(" Are you sure you want to redeem " + _toDisplayString(selectedItem.value?.name) + " for " + _toDisplayString(selectedItem.value?.points) + " points? ", 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_card_actions, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_spacer),
                    _createVNode(_component_v_btn, {
                      color: "grey-darken-1",
                      variant: "text",
                      onClick: _cache[7] || (_cache[7] = ($event: any) => (showConfirmModal.value = false))
                    }, {
                      default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                        _createTextVNode(" Cancel ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(_component_v_btn, {
                      color: "primary",
                      variant: "flat",
                      onClick: confirmRedeem
                    }, {
                      default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                        _createTextVNode(" Confirm ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ])
    ]),
    _: 1
  }))
}
}

})